import React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { monthLabel, monthShort } from '../../../hooks/pipelineTracker/remContract';
import { money, num } from './pipelineParts';
import { Chip, HouseStatCard, type KitIcon } from './trackerKit';
import { deltaSentence, monthShortYear, type PackComparison, type PackComparisonSide } from './boardPackBullets';

/**
 * The board pack's headline: each figure as a house stat card (trackerKit HouseStatCard), its value
 * with a delta on the month before and on the same month last year, and a three-column picture of
 * the three values. These ARE the pack's headline figures (founder, 5 Oct 2026: the old value tiles
 * and these cards showed the same numbers in two styles). The arithmetic and the rules live in
 * boardPackBullets.ts (buildPackComparisons); this file only draws them.
 */

const DIRECTION: Record<'up' | 'down' | 'level', { tone: 'green' | 'red' | 'muted'; icon: KitIcon }> = {
  up: { tone: 'green', icon: ArrowUpRight },
  down: { tone: 'red', icon: ArrowDownRight },
  level: { tone: 'muted', icon: Minus },
};

/** One delta, or the reason there is none. Never a delta for a baseline that was not read. */
export const DeltaChip: React.FC<{ side: PackComparisonSide; vsLabel: string }> = ({ side, vsLabel }) => {
  if (side.status !== 'ok') {
    return (
      <div className="flex items-center justify-between gap-2 text-xs" data-testid="delta">
        <span className="text-gray-500">{vsLabel}</span>
        <span className="text-gray-500">{side.reason}</span>
      </div>
    );
  }
  const { tone, icon: IconC } = DIRECTION[side.direction];
  const amount = side.direction === 'level' ? 'Level' : `${side.direction === 'up' ? '+' : '-'}${money(Math.abs(side.diffCents))}`;
  const sentence = deltaSentence(side, monthLabel(side.month));
  return (
    <div className="flex items-center justify-between gap-2 text-xs" data-testid="delta">
      <span className="text-gray-400">{vsLabel}</span>
      <span aria-label={sentence} title={sentence}>
        <Chip tone={tone}>
          <IconC className="h-3 w-3" aria-hidden="true" />
          {amount}
          {side.direction !== 'level' && side.percent !== null && <span className="opacity-80">{`· ${side.percent}%`}</span>}
        </Chip>
      </span>
    </div>
  );
};

/**
 * Three columns: last year, last month, this month, scaled to the largest. A baseline that was not
 * read is an empty dashed slot, never a zero-height bar that would read as $0.
 */
export const MiniBars: React.FC<{ comparison: PackComparison; month: string; color: string }> = ({ comparison, month, color }) => {
  const slots = [
    { key: 'ly', side: comparison.vsLastYear },
    { key: 'prev', side: comparison.vsPreviousMonth },
  ].map(({ key, side }) => ({
    key,
    label: monthShort(side.month),
    value: side.status === 'ok' ? side.base.valueCents : null,
    current: false,
  }));
  const all = [...slots, { key: 'now', label: monthShort(month), value: comparison.current.valueCents, current: true }];
  const max = Math.max(0, ...all.map((s) => s.value ?? 0));
  return (
    <div className="mt-3 flex h-14 items-end gap-2" role="img" aria-label={`${comparison.label}: ${all.map((s) => `${s.label} ${s.value === null ? 'not available' : money(s.value)}`).join(', ')}`}>
      {all.map((s) => (
        <div key={s.key} className="flex h-full flex-1 flex-col items-center justify-end gap-1" data-testid={`mini-${s.key}`}>
          {s.value === null ? (
            <div className="h-1/2 w-full rounded-t border border-dashed border-gray-600" />
          ) : (
            <div
              className="w-full rounded-t"
              style={{
                height: `${max > 0 ? Math.max(4, (s.value / max) * 100) : 4}%`,
                background: s.current ? `linear-gradient(180deg, ${color}, ${color}99)` : `${color}40`,
              }}
            />
          )}
          <span className={`text-[0.625rem] ${s.current ? 'font-semibold text-gray-200' : 'text-gray-500'}`}>{s.label}</span>
        </div>
      ))}
    </div>
  );
};

/** One headline figure, compared with last month and the same month last year. */
export const ComparisonCard: React.FC<{
  comparison: PackComparison;
  month: string;
  color: string;
  icon: KitIcon;
  /** The basis of the figure, shown in the info tooltip beside the label. */
  info?: React.ReactNode;
  /** The rule-based sentence for this figure, offered on hover: the exact dollars behind the chips. */
  sentence?: string;
}> = ({ comparison, month, color, icon, info, sentence }) => {
  const { current, countNoun } = comparison;
  return (
    <li className="list-none">
      <HouseStatCard
        testId={`compare-${comparison.key}`}
        label={comparison.label}
        value={money(current.valueCents)}
        sub={`${num(current.count)} ${current.count === 1 ? countNoun.one : countNoun.many}`}
        info={info}
        icon={icon}
        color={color}
        title={sentence}
        footer={
          <>
            <div className="space-y-1.5">
              <DeltaChip side={comparison.vsPreviousMonth} vsLabel={`vs ${monthShortYear(comparison.vsPreviousMonth.month)}`} />
              <DeltaChip side={comparison.vsLastYear} vsLabel={`vs ${monthShortYear(comparison.vsLastYear.month)}`} />
            </div>
            <MiniBars comparison={comparison} month={month} color={color} />
          </>
        }
      />
    </li>
  );
};

/** A compact highlight: the month's top settler or top referral partner, as a house stat card. */
export const LeaderCard: React.FC<{
  label: string;
  name: string;
  value: string;
  sub?: string;
  color: string;
  icon: KitIcon;
  testId?: string;
}> = ({ label, name, value, sub, color, icon, testId }) => (
  <li className="list-none">
    <HouseStatCard
      testId={testId}
      label={label}
      value={<span className="block truncate text-xl" data-testid="leader-name">{name}</span>}
      sub={value}
      footer={sub}
      icon={icon}
      color={color}
      valueColor="#f3f4f6"
    />
  </li>
);
